import { useEffect, useRef, useState } from 'react';

// Promise global para cargar Google Maps API una sola vez
let googleMapsLoadingPromise = null;

/**
 * Verifica que Google Maps API esté completamente cargada
 */
const isGoogleMapsReady = () => {
  return window.google && 
         window.google.maps && 
         window.google.maps.Map &&
         typeof window.google.maps.Map === 'function';
};

/**
 * Carga Google Maps API de forma segura (solo una vez)
 */
const loadGoogleMapsAPI = () => {
  // Si ya está completamente cargada, retornar Promise resuelto
  if (isGoogleMapsReady()) {
    return Promise.resolve();
  }

  // Si ya hay una carga en progreso, retornar esa Promise
  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }

  // Crear nueva Promise para cargar el script
  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.error('VITE_GOOGLE_MAPS_API_KEY no está configurada');
      reject(new Error('Google Maps API Key no configurada'));
      return;
    }

    // Verificar si el script ya existe en el DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Si el script existe pero google aún no está disponible, esperar
      const checkGoogle = setInterval(() => {
        if (isGoogleMapsReady()) {
          clearInterval(checkGoogle);
          resolve();
        }
      }, 100);
      
      // Timeout después de 10 segundos
      setTimeout(() => {
        clearInterval(checkGoogle);
        if (!isGoogleMapsReady()) {
          reject(new Error('Timeout esperando Google Maps API'));
        }
      }, 10000);
      
      return;
    }

    // Crear y cargar el script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Esperar a que Map esté disponible (puede tardar un poco con loading=async)
      const checkReady = setInterval(() => {
        if (isGoogleMapsReady()) {
          clearInterval(checkReady);
          resolve();
        }
      }, 50);
      
      // Timeout después de 5 segundos
      setTimeout(() => {
        clearInterval(checkReady);
        if (!isGoogleMapsReady()) {
          googleMapsLoadingPromise = null;
          reject(new Error('Google Maps API no se cargó correctamente - Map no disponible'));
        }
      }, 5000);
    };
    
    script.onerror = () => {
      googleMapsLoadingPromise = null;
      reject(new Error('Error cargando Google Maps API'));
    };
    
    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
};

export const useGoogleMaps = (mapId, center = { lat: 4.6097, lng: -74.0817 }, zoom = 8) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeMap = async () => {
      try {
        // Cargar Google Maps API si no está cargada
        await loadGoogleMapsAPI();

        if (!isMounted) return;

        const mapElement = document.getElementById(mapId);
        if (!mapElement) {
          return;
        }

        // Evitar crear múltiples instancias
        if (window.mapInstances?.[mapId]) {
          setMap(window.mapInstances[mapId]);
          setIsLoaded(true);
          return;
        }

        // Verificar que Google Maps esté listo antes de crear el mapa
        if (!isGoogleMapsReady()) {
          return;
        }

        const mapInstance = new window.google.maps.Map(mapElement, {
          zoom,
          center,
          mapTypeControl: false,
        });

        // Guardar instancia globalmente
        if (!window.mapInstances) {
          window.mapInstances = {};
        }
        window.mapInstances[mapId] = mapInstance;

        if (isMounted) {
          setMap(mapInstance);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error('Error inicializando Google Maps:', error);
      }
    };

    initializeMap();

    return () => {
      isMounted = false;
    };
  }, [mapId, center.lat, center.lng, zoom]);

  return { map, isLoaded };
};
